import { TelemetryIntegration } from '@src/telemetry/telemetry-events.types'

import { CONFIG_NODE_ENV } from '../../config/env'
import { TELEMETRY_DOMAINS } from '../telemetry-domains.constants'
import { TELEMETRY_EVENT_NAMES } from '../telemetry-events.constants'

import { removeHyphensFromEventId } from './provider-utils/data-layer-pixel-utils'
import { initializeScript } from './provider-utils/initialize-script'

const METHODS = [
  'page',
  'track',
  'identify',
  'instances',
  'debug',
  'on',
  'off',
  'once',
  'ready',
  'alias',
  'group',
  'enableCookie',
  'disableCookie',
] as const
type MethodNames = (typeof METHODS)[number]
type MethodsType = (...args: unknown[]) => void

type TrackingFunction = Omit<
  {
    [k in MethodNames]?: MethodsType
  },
  'page' | 'track'
>

export type TickTokTrackingGlobalObject = unknown[] &
  TrackingFunction &
  Partial<{
    methods: readonly MethodNames[]
    setAndDefer: (trackingObject: TickTokTrackingGlobalObject, methodName: MethodNames) => void
    instance: (sdkId: string) => unknown[]
    load: (sdkId: string, n?: Record<string, unknown>) => void
    _i: Record<string, TickTokTrackingGlobalObject>
    _t: Record<string, number>
    _o: Record<string, unknown>
    _u: string
    // Tracking Methods
    page: () => void
    track: (
      eventType: unknown,
      trackingData: {
        content_id: unknown
        content_type: string
        content_name: string
        content_category: unknown
        currency: string
        value: number
        quantity: number
      },
      eventDeduplication: {
        event_id?: string
      },
    ) => void
  }>

export const TikTokPixel: TelemetryIntegration = {
  config: {
    enabled: CONFIG_NODE_ENV === 'test' ? false : true,
    eventIdealTimeout: 1000,
    domain: TELEMETRY_DOMAINS.TIKTOK_PIXEL,
  },

  initialize: async () => {
    await initializeScript({
      key: 'tiktok',
      domain: TELEMETRY_DOMAINS.TIKTOK_PIXEL,
      path: '/i18n/pixel/events.js?sdkid=CA5Q463C77UAJ54RTKKG&lib=ttq',
      injectAt: 'beforeFirstScript',
      init: () => {
        window.TiktokAnalyticsObject = 'ttq'
        const ttq: TickTokTrackingGlobalObject = (window.ttq = window.ttq ?? [])
        ttq.methods = METHODS
        ttq.setAndDefer = function (trackingObject, methodName) {
          trackingObject[methodName] = function (...args: unknown[]) {
            trackingObject.push([methodName, ...args])
          }
        }
        for (const method of ttq.methods) {
          ttq.setAndDefer(ttq, method)
        }
        ttq.instance = function (sdkId) {
          const instanceTrackingObject = ttq._i?.[sdkId] ?? []
          for (const method of ttq.methods ?? METHODS) {
            if (ttq.setAndDefer) {
              ttq.setAndDefer(instanceTrackingObject, method)
            }
          }
          return instanceTrackingObject
        }
        ttq.load = function (sdkId, n) {
          const i = 'https://analytics.tiktok.com/i18n/pixel/events.js'
          ttq._i = ttq._i ?? {}
          ttq._i[sdkId] = []
          ttq._i[sdkId]._u = i
          ttq._t = ttq._t ?? {}
          ttq._t[sdkId] = +new Date()
          ttq._o = ttq._o ?? {}
          ttq._o[sdkId] = n ?? {}
        }

        ttq.load('CA5Q463C77UAJ54RTKKG')
        if (ttq.page) {
          ttq.page()
        }
      },
    })
  },

  events: {
    [TELEMETRY_EVENT_NAMES.ADD_TO_CART]: properties => {
      // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
      const eventId = properties?.eventId?.toString() || ''
      const alphanumericEventId = removeHyphensFromEventId(eventId)
      logTikTokPixelEvent({
        eventType: 'AddToCart',
        contentId: properties.productId,
        contentType: 'product_group',
        contentName:
          properties.productTitle && properties.variantTitle
            ? properties.productTitle + ' - ' + properties.variantTitle
            : '',
        contentCategory: properties.productType,
        currency: 'USD',
        value: properties.price ? formatRevenue(Number(properties.price)) : 0,
        numItems: properties.quantity || 1,
        eventId: alphanumericEventId,
      })
    },

    [TELEMETRY_EVENT_NAMES.PRODUCT_VIEW]: properties => {
      // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
      const eventId = properties?.eventId?.toString() || ''
      const alphanumericEventId = removeHyphensFromEventId(eventId)
      logTikTokPixelEvent({
        eventType: 'ViewContent',
        contentId: properties.productId.toString(),
        contentType: 'product_group',
        contentName:
          properties.productTitle && properties.variantTitle
            ? properties.productTitle + ' - ' + properties.variantTitle
            : '',
        contentCategory: properties.productType,
        currency: 'USD',
        value: properties.price ? formatRevenue(Number(properties.price)) : 0,
        numItems: 1,
        eventId: alphanumericEventId,
      })
    },

    [TELEMETRY_EVENT_NAMES.PAGE_VIEW]() {
      if (window.ttq && window.ttq.page) window.ttq.page()
    },
  },
}

export const logTikTokPixelEvent = ({
  eventType,
  contentId,
  contentType,
  contentName,
  contentCategory,
  currency,
  value,
  numItems,
  eventId,
}: {
  eventType: string
  contentId: string
  contentType: string
  contentName: string
  contentCategory: string
  currency: string
  value: number
  numItems: number
  eventId?: string
}) => {
  if (window.ttq && window.ttq.track) {
    window.ttq.track(
      eventType,
      {
        content_id: contentId,
        content_type: contentType,
        content_name: contentName,
        content_category: contentCategory,
        currency: currency,
        value: value,
        quantity: numItems,
      },
      {
        event_id: eventId,
      },
    )
  } else {
    console.log('ttq is not available on window')
  }
}

export const formatRevenue = (price: number | null): number => {
  if (price != null) {
    return price
  }
  return 0
}
